import { useEffect, useRef } from "react";

import type { EnrollmentJourney, EnrollmentProperties } from "./enrollment";

interface FormState {
    mode: "stream" | "claim";
    address: string;
    connectedWallet: string;
    label: string;
    addressValid: boolean;
    termsVersion?: string;
    termsError: boolean;
    termsLoading: boolean;
    termsAccepted: boolean;
    destinationState: string;
    destinationAllowed: boolean;
    destinationFailed: boolean;
    inFlight: boolean;
    hasPendingProof: boolean;
}
/** Observe visible form states; raw values only participate in local deduplication. */
export function useEnrollmentFormAnalytics(
    journey: EnrollmentJourney | undefined,
    state: FormState
): {
    commitDestination: () => void;
    retryCheck: (type: "terms" | "destination") => void;
} {
    const destination = useRef(JSON.stringify([state.address.trim(), state.label]));
    const latest = useRef(state);
    latest.current = state;
    useEffect(() => {
        if (!journey) return;
        journey.updateForm({
            dirty:
                state.mode !== "stream" ||
                state.address !== state.connectedWallet ||
                !!state.label ||
                state.termsAccepted
        });
        journey.view({
            distribution_mode: state.mode,
            destination_kind:
                state.address.trim() === "" || state.address.trim() === state.connectedWallet ? "self" : "custom",
            has_pending_proof: state.hasPendingProof
        });
        const reasons = [
            !state.termsVersion && "terms_unavailable",
            !state.termsAccepted && "terms_unchecked",
            !state.addressValid && "invalid_address",
            !state.destinationAllowed && "destination_unavailable",
            state.inFlight && "request_pending"
        ].filter((value): value is string => typeof value === "string");
        journey.changed("enroll:validation_updated", { can_submit: reasons.length === 0, blocking_reasons: reasons });
    }, [
        journey,
        state.mode,
        state.address,
        state.connectedWallet,
        state.label,
        state.addressValid,
        state.termsVersion,
        state.termsLoading,
        state.termsError,
        state.termsAccepted,
        state.destinationState,
        state.destinationAllowed,
        state.destinationFailed,
        state.inFlight,
        state.hasPendingProof
    ]);
    return {
        commitDestination: (): void => {
            const current = latest.current;
            const key = JSON.stringify([current.address.trim(), current.label]);
            if (destination.current === key) return;
            destination.current = key;
            const properties: EnrollmentProperties = {
                destination_kind:
                    !current.address.trim() || current.address.trim() === current.connectedWallet ? "self" : "custom",
                has_label: !!current.label.trim(),
                address_valid: current.addressValid
            };
            journey?.send("enroll:destination_updated", properties);
        },
        retryCheck: (type): void => {
            journey?.send("enroll:retry_clicked", { retry_target: type, retry_action: "retry_check" });
        }
    };
}
